import { ChangeDetectionStrategy, Component, computed, inject, model } from '@angular/core'
import { SearchBarComponent } from '@shared/components/search-bar/search-bar.component'
import { ButtonDirective } from 'primeng/button'
import { Router, RouterLink } from '@angular/router'
import { AuthService } from '@app/core/auth/services/auth.service'
import { OverlayBadge } from 'primeng/overlaybadge'
import { Avatar } from 'primeng/avatar'
import { FeatureFlagService } from '@services/feature-flag.service'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import { Toolbar } from 'primeng/toolbar'
import { Menu } from 'primeng/menu'
import { MenuItem } from 'primeng/api'
import { CategoryService } from '@app/core/auth/services/category.service'
import { RoleService } from '@app/core/auth/services/role.service'

@Component({
  selector: 'albert-header',
  imports: [SearchBarComponent, ButtonDirective, RouterLink, Toolbar, Avatar, OverlayBadge, NgClass, NgTemplateOutlet, Menu],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  public readonly sidebarDrawerVisible = model(true)
  public readonly toolbarDrawerVisible = model(false)
  public readonly featureFlagService = inject(FeatureFlagService)
  private readonly authService = inject(AuthService)
  public readonly userInitials = computed(() => {
    const givenName = this.authService.userData()?.given_name ?? ''
    const familyName = this.authService.userData()?.family_name ?? ''
    return givenName.charAt(0) + familyName.charAt(0)
  })
  private readonly categoryService = inject(CategoryService)
  private readonly roleService = inject(RoleService)
  private readonly router = inject(Router)
  public readonly menuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = []

    // Categories Section
    const availableCategories = this.categoryService.availableCategories()
    if (availableCategories.length > 0) {
      items.push({
        label: 'Category',
        items: availableCategories.map(cat => ({
          label: cat.label,
          ...(this.categoryService.activeCategory() === cat.value ? { icon: 'pi pi-check' } : {}),
          command: async () => {
            this.categoryService.setCategory(cat.value)
            await this.router.navigate(['/'])
          }
        }))
      })
    }

    // Roles Section
    const availableRoles = this.roleService.availableRoles()
    if (availableRoles.length > 0) {
      items.push({
        label: 'Roles',
        items: availableRoles.map(role => ({
          label: role.label,
          disabled: !role.selectable,
          ...(this.roleService.activeRole() === role.value ? { icon: 'pi pi-check' } : {}),
          command: () => this.roleService.setRole(role.value)
        }))
      })
    }

    // State Section
    items.push({
      label: 'State',
      items: [
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: () => this.logout()
        }
      ]
    })

    return items
  })

  public openSidebarDrawer(): void {
    this.sidebarDrawerVisible.set(true)
  }

  public openToolbarDrawer(): void {
    this.toolbarDrawerVisible.set(true)
  }

  public logout(): void {
    if (this.featureFlagService.featureFlags()['manual_logout']) {
      this.authService.logout()
    }
  }
}
